import type { ComponentType, ReactNode } from 'react';
import { isValidElement, useMemo } from 'react';

type LabelInput = ReactNode | (() => ReactNode);

interface UseProcessedLabelOptions<P = any> {
  label: LabelInput;
  WrapperComponent: ComponentType<P>;
  wrapperProps?: Omit<P, 'children'>;
}

/**
 * Custom hook to process label inputs with memoization for optimal performance.
 * Handles four cases:
 * 1. If label is nullish, it renders nothing — no empty wrapper element
 * 2. If label is a function, it calls the function
 * 3. If label is already an instance of WrapperComponent, it returns it as-is
 * 4. Otherwise, it wraps the label in the WrapperComponent
 *
 * @param options - Configuration object containing label, WrapperComponent, and optional wrapper props
 * @returns Processed label element
 */
export function useProcessedLabel<P = any>({
  label,
  WrapperComponent,
  wrapperProps,
}: UseProcessedLabelOptions<P>): ReactNode {
  return useMemo(() => {
    if (label === undefined || label === null) {
      return null;
    }
    if (typeof label === 'function') {
      return label();
    }
    if (isValidElement(label) && label.type === WrapperComponent) {
      return label;
    }
    return (
      <WrapperComponent {...(wrapperProps as P)}>{label}</WrapperComponent>
    );
  }, [label, WrapperComponent, wrapperProps]);
}
